import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Link } from 'react-router-dom'
import { useTeam } from 'app/frontend/base/config/context/team'
import ApiKeyField from 'app/frontend/base/components/form-fields/ApiKeyField'

interface Props {
  open: boolean
  setOpen: (open: boolean) => void
}

const DiscordBotInstructionModal = ({ open, setOpen }: Props) => {
  const { team } = useTeam()

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='max-w-3xl'>
        <DialogHeader>
          <DialogTitle>Discord bot instructions</DialogTitle>
        </DialogHeader>

        <div className='flex flex-col gap-4'>
          <p>
            After installing the bot, you should run the <strong>/setup</strong>{' '}
            command. This will link your Discord server to your wowaudit team.
            Use this API key: <ApiKeyField name='privateKey' readonly />
            You can use the <em>restricted_to_channel</em> option to set up the
            bot only in a specific channel, allowing you to have the bot work
            for multiple teams in one server. Once you have linked your server,
            these are the most important commands:
          </p>

          <ul className='list-disc list-inside [&>li]:pl-4 [&>li]:text-indent-0 [&>li]:[text-indent:-1.25rem]'>
            <li className='pl-4 mb-4'>
              <strong>/subscribe</strong>: Configure a channel to automatically
              have raids posted a number of days in advance. You can also let
              the bot mention a role when new raids are posted. Raid messages
              will be automatically deleted once they are in the past. Raids are
              posted 5 minutes past the end time hour of the raid, the amount of
              days in advance that you configured. For example, when a raid ends
              at 22:00 the bot will post it at 22:05 by default 7 days in
              advance. You can use the <em>day_of_the_week</em> option to only
              post raids that happen on a specific day of the week.
            </li>

            <li className='pl-4 mb-4'>
              <strong>/raid</strong>: Post a single raid event from your
              wowaudit calendar in this channel. Through this message users (and
              guests, if enabled) can change their signup status, set a comment,
              and view most information related to the raid. Players who
              aren&apos;t selected on any encounter will be shown separately as{' '}
              <em>queued</em>. It looks like this:
              <figure className='my-4 max-w-[30rem]'>
                <img src='https://data.wowaudit.com/img/discord-bot-raid-v2.png' />
              </figure>
              In addition to being able to view personal selections with the
              button, the thumbnail of the post is an img that shows the
              up-to-date selections of all players for each boss. It looks like
              this:
              <figure className='my-4 max-w-[30rem]'>
                <img src='https://data.wowaudit.com/img/discord-bot-selections.png' />
              </figure>
              You can enable reminders to be sent to users who haven&apos;t
              signed up yet, in the{' '}
              <Link
                className='text-primary'
                to={`${team?.path}/settings#notifications`}
              >
                notification settings
              </Link>
              . You can also configure options in the settings to allow guests
              to sign up.
            </li>

            <li className='pl-4 mb-4'>
              <strong>/create_raid</strong>: Create a single raid event on a
              specific date. You must specify the date, start time, end time,
              and instance. Requires you to have admin access on wowaudit.
            </li>
          </ul>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type='button' variant='secondary'>
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default DiscordBotInstructionModal
